import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Box, TablePagination, Card, Tabs, Tab, Grid, Typography, MenuItem, Select, TextField,
    FormControl, InputLabel, Button, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Table, CircularProgress, Container, InputAdornment
} from "@mui/material";
import { makeApiRequest } from '../../../utils/api';
import { trimSeconds, formatTimeToHoursMinutes, formatDate } from '../../../helpers/timeHelper';
import { getLocationList } from '../../../helpers/commonHelper';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { jsPDF } from "jspdf";
import { Search } from "@mui/icons-material";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import "jspdf-autotable";
import * as XLSX from 'xlsx';
import GridOnIcon from '@mui/icons-material/GridOn';

const ViewBranchAdminTask = () => {
    const todayDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const [month, day, year] = todayDate.split('/');
    const formattedDate = `${year}-${month}-${day}`;
    var today_date = formattedDate;

    const [value, setValue] = useState('today');
    const [location, setLocation] = useState("All Locations");
    const [staffName, setStaffName] = useState('');
    const [startDate, setStartDate] = useState(today_date);
    const [endDate, setEndDate] = useState(today_date);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [learningData, setLearningData] = useState([]);
    const [locations, setLocations] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [totalRecords, setTotalRecords] = useState(0);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const listLocation = () => {
        const locationList = getLocationList();
        setLocations(locationList);
    };

    const getBranchAdminListForLocation = async (loc) => {
        try {
            const response = await makeApiRequest('post', '/api/team/list', {
                token: localStorage.getItem('authToken'),
                data: {
                    control: 4,
                    location: loc === 'All Locations' ? '' : loc,
                    per_page: 500,
                }
            });

            if (response.status === 'success') {
                setStaffList(response.data.list || []);
            } else {
                throw new Error('Failed to fetch branch admin list');
            }
        } catch (err) {
            console.error('Error fetching branch admin list:', err);
            setError(err.response?.data?.message || 'Error fetching branch admin list');
        }
    };

    const fetchTaskData = async (start, end) => {
        try {
            setLoading(true);
            setError(null);
            setHasSubmitted(true);

            const response = await makeApiRequest('post', '/api/learning/staff', {
                token: localStorage.getItem('authToken'),
                data: {
                    start_date: start,
                    end_date: end,
                    location: location,
                    name: staffName,
                    control: 4,
                },
                page: page + 1,
                per_page: rowsPerPage
            });

            if (response.status === 'success') {
                setLearningData(response.data.data);

                const pagination = response.data.pagination || {};
                setTotalRecords(pagination.total || response.data.data.length);
            } else {
                setLearningData([]);
                throw new Error('Invalid data received');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.response?.data?.message || 'Error fetching data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        listLocation();
        getBranchAdminListForLocation('All Locations');
        fetchTaskData(today_date, today_date);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchTaskData(startDate, endDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    const handleChange = (event, newValue) => {
        setHasSubmitted(false);
        setLocation("All Locations");
        setStaffName("");
        setLearningData([]);
        setPage(0);

        if (newValue === 'today') {
            setStartDate(today_date);
            setEndDate(today_date);
            fetchTaskData(today_date, today_date);
        } else {
            setStartDate("");
            setEndDate("");
        }
        setValue(newValue);
    };

    const handleSubmit = async () => {
        if (value === 'historical' && (startDate === '' || endDate === '')) {
            toast.warn("Please choose start date and end date");
            return;
        }
        setPage(0);
        fetchTaskData(startDate, endDate);
    };

    const handleLocationChange = (event) => {
        const loc = event.target.value;
        setLocation(loc);
        setStaffName("");
        getBranchAdminListForLocation(loc);
    };

    const handleStaffChange = (event) => {
        setStaffName(event.target.value);
    };

    const getFilteredData = (data) => {
        if (!searchText || !Array.isArray(data)) return data;

        return data.filter(item => {
            const searchLower = searchText.toLowerCase();
            return (
                (String(item.name || '').toLowerCase().includes(searchLower)) ||
                (String(item.user_id || '').toLowerCase().includes(searchLower)) ||
                (String(item.location || '').toLowerCase().includes(searchLower)) ||
                (String(item.task || '').toLowerCase().includes(searchLower)) ||
                (String(item.desc || '').toLowerCase().includes(searchLower))
            );
        });
    };

    const fetchAllTaskData = async () => {
        try {
            const response = await makeApiRequest('post', '/api/learning/staff/all', {
                token: localStorage.getItem('authToken'),
                data: {
                    start_date: startDate,
                    end_date: endDate,
                    location: location,
                    name: staffName,
                    control: 4,
                }
            });

            if (response.status === 'success') {
                return response.data.data || [];
            } else {
                throw new Error('Invalid data received');
            }
        } catch (err) {
            console.error('Error fetching all data:', err);
            toast.error('Error fetching data for export');
            return [];
        }
    };

    const handleExport = async () => {
        const data = await fetchAllTaskData();
        if (!data || data.length === 0) {
            toast.warn("No data available to export.");
            return;
        }

        const doc = new jsPDF();
        const title = value === "today" ? "Branch Admin Task Report (Today)" : "Branch Admin Task Report (Historical)";
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text(title, 14, 15);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);

        const headers = [["Branch Admin", "Date", "Duration", "Task", "Description"]];
        const body = data.map(item => [
            `${item.name || '-'} (${item.location || 'Unknown'} | ${item.user_id || '-'})`,
            formatDate(item.date) || '-',
            `${formatTimeToHoursMinutes(item.duration) || '-'} (${trimSeconds(item.in_time) || '-'} to ${trimSeconds(item.out_time) || '-'})`,
            item.task || '-',
            item.desc || '-'
        ]);

        doc.autoTable({
            startY: 30,
            head: headers,
            body: body,
            theme: 'grid',
            styles: { font: 'helvetica', fontSize: 10, cellPadding: 3, textColor: [0, 0, 0], lineColor: [200, 200, 200], lineWidth: 0.1 },
            headStyles: { fillColor: [70, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            margin: { top: 30, left: 14, right: 14 }
        });

        doc.save(`${value === "today" ? "today" : "historical"}-branch-admin-task-report.pdf`);
    };

    const handleExportExcel = async () => {
        const data = await fetchAllTaskData();
        if (!data || data.length === 0) {
            toast.warn("No data available to export.");
            return;
        }

        const headers = ["Branch Admin", "Date", "Duration", "Task", "Description"];
        const worksheetData = [
            headers,
            ...data.map(item => [
                `${item.name || '-'} (${item.location || 'Unknown'} | ${item.user_id || '-'})`,
                formatDate(item.date) || '-',
                `${formatTimeToHoursMinutes(item.duration) || '-'} (${trimSeconds(item.in_time) || '-'} to ${trimSeconds(item.out_time) || '-'})`,
                item.task || '-',
                item.desc || '-'
            ])
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        worksheet['!cols'] = [{ wch: 40 }, { wch: 15 }, { wch: 30 }, { wch: 20 }, { wch: 30 }];

        XLSX.utils.book_append_sheet(workbook, worksheet, "Branch Admin Task Report");
        XLSX.writeFile(workbook, `${value === "today" ? "today" : "historical"}-branch-admin-task-report.xlsx`);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const convertDurationToMinutes = (duration) => {
        if (!duration) return 0;
        const [hours, minutes] = duration.split(':').map(Number);
        return (hours * 60) + minutes;
    };

    const getSortedData = (data) => {
        if (!sortConfig.key || !Array.isArray(data)) return data;

        return [...data].sort((a, b) => {
            let aValue, bValue;
            switch (sortConfig.key) {
                case 'name':
                    aValue = (a.name || '').toLowerCase();
                    bValue = (b.name || '').toLowerCase();
                    break;
                case 'date':
                    aValue = new Date(a.date);
                    bValue = new Date(b.date);
                    break;
                case 'duration':
                    aValue = convertDurationToMinutes(a.duration);
                    bValue = convertDurationToMinutes(b.duration);
                    break;
                default:
                    aValue = a[sortConfig.key];
                    bValue = b[sortConfig.key];
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={40} thickness={4} sx={{ color: '#481E82' }} />
        </Box>
    );

    return (
        <>
            <ToastContainer position="top-center" autoClose={2000} theme="light" />
            <Box sx={{ minHeight: '100vh', backgroundColor: '#ECEDFF', width: "100%", maxHeight: 'auto' }}>
                <Container maxWidth='xl'>
                    <Grid container spacing={2}>
                        <Grid item xs={12} style={{ height: "80px", display: "flex", alignItems: "center" }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontFamily: "Roboto",
                                    fontSize: { xs: '24px', sm: "34px" },
                                    fontStyle: "normal",
                                    fontWeight: "500",
                                    lineHeight: "160%",
                                    letterSpacing: "0.15px",
                                    color: "#481E82",
                                }}
                            >
                                Branch Admin Task List
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Box>
                                <Card sx={{ display: "flex", justifyContent: { xs: 'flex-start', sm: "flex-start" }, alignItems: "center", height: "100%" }}>
                                    <Tabs value={value} onChange={handleChange} aria-label="centered tabs example" centered>
                                        <Tab label="Today" value='today' sx={{ fontWeight: value === 'today' ? 'bold' : 'normal' }} />
                                        <Tab label="Historical" value='historical' sx={{ fontWeight: value === 'historical' ? 'bold' : 'normal' }} />
                                    </Tabs>
                                </Card>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            {value === 'today' && (
                                <Grid container spacing={2} sx={{ paddingLeft: 0, paddingRight: 0, marginTop: "1px" }}>
                                    <Grid item xs={12}>
                                        <Card sx={{ px: 2, py: 3 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={7}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6} md={6} lg={5}>
                                                            <FormControl fullWidth sx={{ maxWidth: "100%" }} size="small">
                                                                <InputLabel id="dropdown-label">Select Location</InputLabel>
                                                                <Select
                                                                    labelId="dropdown-label"
                                                                    id="dropdown"
                                                                    value={location}
                                                                    onChange={handleLocationChange}
                                                                    label="Select Location"
                                                                >
                                                                    <MenuItem value="All Locations">All Locations</MenuItem>
                                                                    {locations && locations.map((loc) => (
                                                                        <MenuItem key={loc.id} value={loc.primary_location}>
                                                                            {loc.primary_location}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={6} lg={5}>
                                                            <FormControl fullWidth sx={{ maxWidth: "100%" }} size="small">
                                                                <InputLabel id="dropdown-label">Select Branch Admin</InputLabel>
                                                                <Select
                                                                    labelId="dropdown-label"
                                                                    id="dropdown"
                                                                    value={staffName}
                                                                    onChange={handleStaffChange}
                                                                    label="Select Branch Admin"
                                                                >
                                                                    <MenuItem value="">All Branch Admins</MenuItem>
                                                                    {staffList.map((staffMember, index) => (
                                                                        <MenuItem key={index} value={staffMember.userid}>
                                                                            {staffMember.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12} sm={5} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <Button variant="contained" onClick={handleSubmit} size='medium'>
                                                        Submit
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}
                            {value === 'historical' && (
                                <Grid container spacing={2} sx={{ paddingLeft: 0, paddingRight: 0, marginTop: "0" }}>
                                    <Grid item xs={12}>
                                        <Card sx={{ padding: 2, height: "auto" }}>
                                            <Grid container spacing={2} sx={{ marginTop: '10px' }}>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        label="Start Date"
                                                        type="date"
                                                        variant="outlined"
                                                        InputLabelProps={{ shrink: true }}
                                                        size='small'
                                                        sx={{ width: "100%" }}
                                                        value={startDate}
                                                        onChange={(event) => setStartDate(event.target.value)}
                                                        onFocus={(e) => e.target.showPicker()}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        label="End Date"
                                                        type="date"
                                                        variant="outlined"
                                                        InputLabelProps={{ shrink: true }}
                                                        size='small'
                                                        sx={{ width: "100%" }}
                                                        value={endDate}
                                                        onChange={(event) => setEndDate(event.target.value)}
                                                        onFocus={(e) => e.target.showPicker()}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <FormControl fullWidth sx={{ maxWidth: "100%" }} size="small">
                                                        <InputLabel id="dropdown-label">Select Location</InputLabel>
                                                        <Select
                                                            labelId="dropdown-label"
                                                            id="dropdown"
                                                            value={location}
                                                            onChange={handleLocationChange}
                                                            label="Select Location"
                                                        >
                                                            <MenuItem value="All Locations">All Locations</MenuItem>
                                                            {locations && locations.map((loc) => (
                                                                <MenuItem key={loc.id} value={loc.primary_location}>
                                                                    {loc.primary_location}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <FormControl fullWidth sx={{ maxWidth: "100%" }} size="small">
                                                        <InputLabel id="dropdown-label">Select Branch Admin</InputLabel>
                                                        <Select
                                                            labelId="dropdown-label"
                                                            id="dropdown"
                                                            value={staffName}
                                                            onChange={handleStaffChange}
                                                            label="Select Branch Admin"
                                                        >
                                                            <MenuItem value="">All Branch Admins</MenuItem>
                                                            {staffList.map((staffMember, index) => (
                                                                <MenuItem key={index} value={staffMember.userid}>
                                                                    {staffMember.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                            <Box sx={{ display: "flex", justifyContent: { xs: 'center', sm: "flex-end" }, marginTop: 3 }}>
                                                <Button variant="contained" size="medium" onClick={handleSubmit}>
                                                    Submit
                                                </Button>
                                            </Box>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ mt: 1 }}>
                                <Box sx={{ maxWidth: '100%', width: '100%', backgroundColor: 'white', borderRadius: 1, boxShadow: 3, position: 'relative' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', sm: "row" }, padding: '20px 15px' }}>
                                        <Grid container spacing={2} sx={{ justifyContent: "space-between" }}>
                                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                                <TextField
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                    size='small'
                                                    placeholder="Search"
                                                    sx={{ width: "100%" }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Search />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={7} md={6}>
                                                <Grid container spacing={0} sx={{ justifyContent: "flex-end" }}>
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleExport}
                                                        startIcon={<PictureAsPdfIcon />}
                                                        disabled={!learningData.length}
                                                        sx={{ backgroundColor: "orange", width: { xs: "100%", sm: "fit-content" }, marginRight: { xs: 0, sm: 2 } }}>
                                                        Download as PDF
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleExportExcel}
                                                        startIcon={<GridOnIcon />}
                                                        disabled={!learningData.length}
                                                        sx={{ backgroundColor: "#4CAF50", "&:hover": { backgroundColor: "#45A049" }, width: { xs: "100%", sm: "fit-content" }, marginTop: { xs: 1, sm: 0 } }}
                                                    >
                                                        Export to EXCEL
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <TableContainer sx={{ width: '100%', overflowX: "auto", marginTop: 0 }}>
                                        <Table>
                                            <TableHead sx={{ backgroundColor: '#f1f4f9' }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: "bold", color: "black" }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('name')}>
                                                            <strong>Branch Admin</strong>
                                                            {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: "bold", color: "black" }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('date')}>
                                                            <strong>Date</strong>
                                                            {sortConfig.key === 'date' && (sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: "bold", color: "black" }}><strong>Duration</strong></TableCell>
                                                    <TableCell sx={{ fontWeight: "bold", color: "black" }}><strong>Task</strong></TableCell>
                                                    <TableCell sx={{ fontWeight: "bold", color: "black" }}><strong>Description</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {hasSubmitted ? (
                                                    getSortedData(getFilteredData(learningData)).length > 0 ? (
                                                        getSortedData(getFilteredData(learningData)).map((row, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell>
                                                                    {row.name}
                                                                    <br />
                                                                    <Typography variant="body2" sx={{ fontSize: '13px', color: 'gray' }}>
                                                                        {row.location} | {row.user_id}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>{formatDate(row.date)}</TableCell>
                                                                <TableCell>
                                                                    {formatTimeToHoursMinutes(row.duration)}
                                                                    <br />
                                                                    <Typography variant="body2" sx={{ fontSize: '13px', color: 'gray' }}>
                                                                        {trimSeconds(row.in_time)} to {trimSeconds(row.out_time)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>{row.task}</TableCell>
                                                                <TableCell>{row.desc}</TableCell>
                                                            </TableRow>
                                                        ))
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell colSpan={5} align="center">
                                                                No data available
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={5} align="center">
                                                            Please click submit to view task data
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        component="div"
                                        count={totalRecords}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    );
};

export default ViewBranchAdminTask;
